// src/pages/GearDetail.jsx

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  ArrowLeft,
  ClipboardList,
  FileText,
  Image as ImageIcon,
  File,
  AlertTriangle,
  Trash2,
  Repeat,
  Settings,
} from "lucide-react";
import { useTeamStore } from "../store/teamStore";
import { useProjectStore } from "../store/projectStore";
import { api } from "../lib/api";
import { useGearStore } from "../store/gearStore"; // <-- add this
import TransferEquipmentModal from "../components/TransferEquipmentModal";
import GearTabs from "../components/gear/GearTabs";
import GearModal from "../components/GearModal";

const TAB_LIST = [
  { label: "Overview", key: "overview", icon: <ClipboardList size={18} /> },
  { label: "Activity", key: "activity", icon: <FileText size={18} /> },
  { label: "Documents", key: "documents", icon: <File size={18} /> },
  { label: "Images", key: "images", icon: <ImageIcon size={18} /> },
];

export default function GearDetail({ gear, setCurrentPage, setTopbarCenter }) {
  const { team } = useTeamStore();
  const { projects, fetchProjects } = useProjectStore();
  const teamList = Array.isArray(team)
    ? team
    : Array.isArray(team?.members)
    ? team.members
    : Array.isArray(team?.items)
    ? team.items
    : [];
  const projectsList = useMemo(
    () => (Array.isArray(projects) ? projects : []),
    [projects]
  );
  const [tab, setTab] = useState("overview");
  const [pendingAction, setPendingAction] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Desktop: render these tabs in the global TopBar row (same row as notification bell)
  useEffect(() => {
    if (typeof setTopbarCenter !== "function") return;
    if (typeof window === "undefined") return;
    if (window.innerWidth < 768) return; // md breakpoint: keep tabs in-page on mobile

    setTopbarCenter(
      <GearTabs
        tabs={TAB_LIST}
        activeTab={tab}
        onChange={setTab}
        variant="bare"
      />
    );

    return () => setTopbarCenter(null);
  }, [tab, setTopbarCenter]);
  const [pendingHandled, setPendingHandled] = useState(false);

  // derive current user id from backend-authenticated local storage (no Firebase)
  const currentUserId = JSON.parse(localStorage.getItem('user') || '{}')?.id || '';

  const { gear: gearList, setGear, categories, statuses, fields } = useGearStore(); // global list + setter

  const [showEditModal, setShowEditModal] = useState(false);
  const editModalRef = useRef(null);

  const [currentUserRole, setCurrentUserRole] = useState("user");
  useEffect(() => {
    try {
      const me = JSON.parse(localStorage.getItem("user") || "null");
      setCurrentUserRole(me?.role || "user");
    } catch {
      setCurrentUserRole("user");
    }
  }, []);
  const isAdminOrManager = ["admin", "manager"].includes(String(currentUserRole || "").toLowerCase());

  // --- Local gear state for instant updates ---
  const [localGear, setLocalGear] = useState(gear);
  // Keep in sync with parent if gear prop changes
  useEffect(() => { setLocalGear(gear); }, [gear]);

  // Always fetch fresh gear detail (ensures activity + pendingTransfer + isArchived are current)
  useEffect(() => {
    if (!gear?.id) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/gear/${gear.id}`);
        const fresh = res?.data && typeof res.data === "object" ? res.data : res;

        if (cancelled || !fresh) return;

        setLocalGear(fresh);

        // keep global store in sync too
        setGear((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const idx = list.findIndex((x) => x.id === fresh.id);
          if (idx === -1) return list;
          const copy = list.slice();
          copy[idx] = { ...copy[idx], ...fresh };
          return copy;
        });
      } catch (e) {
        // non-blocking (fallback to whatever we already have)
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gear?.id]);

  // Fetch projects if not loaded
  useEffect(() => {
    if (projectsList.length === 0) {
      fetchProjects && fetchProjects();
    }
  }, [projectsList, fetchProjects]);

  function displayUser(id) {
    if (!id) return "—";
    const user = teamList.find((u) => u.id === id);
    return user ? user.name || user.email || id : id;
  }
  function displayProject(id) {
    if (!id) return "—";
    const project = projectsList.find((p) => p.id === id);
    return project ? project.name || id : id;
  }

  // Save edits (PATCH) + update store + update localGear
  const handleEditSave = async (data) => {
    if (!localGear?.id) throw new Error("Missing gear id.");
    if (!isAdminOrManager) throw new Error("You don't have permission to edit gear.");

    // api may return axios-like { data } OR the JSON object directly
    const unwrap = (res) => {
      const payload = res && typeof res === "object" && "data" in res ? res.data : res;
      return payload && typeof payload === "object" ? payload : null;
    };

    const res = await api.patch(`/gear/${localGear.id}`, data);
    const updated = unwrap(res) || { ...localGear, ...data };

    setGear((prev) =>
      (Array.isArray(prev) ? prev : []).map((g) =>
        g.id === localGear.id ? { ...g, ...updated } : g
      )
    );
    setLocalGear((prev) => ({ ...(prev || {}), ...updated }));
  };

  // Transfer handler for TransferEquipmentModal (store-based, no Firebase)
  async function handleGearTransfer(gearId, newAssignedTo, newProject) {
    const notifId = `${gearId}_${Date.now()}`;

    // Read current gear from global store (fallback to localGear)
    const current = (Array.isArray(gearList) ? gearList : []).find((g) => g.id === gearId) || localGear;
    if (!current) return;

    const from = {
      userId: current.assignedTo || "",
      projectId: current.assignedProject || "",
    };
    const to = {
      userId: newAssignedTo,
      projectId: newProject || "",
    };

    // Update global store: add pendingTransfer
    setGear((prev) => {
      if (!Array.isArray(prev)) return [];
      return prev.map((g) =>
        g.id === gearId
          ? {
              ...g,
              pendingTransfer: {
                notifId,
                to,
                from,
                date: new Date().toISOString(),
                requestedBy: currentUserId,
              },
            }
          : g
      );
    });

    // Update local detail view, too
    setLocalGear((lg) =>
      lg && lg.id === gearId
        ? {
            ...lg,
            pendingTransfer: {
              notifId,
              to,
              from,
              date: new Date().toISOString(),
              requestedBy: currentUserId,
            },
          }
        : lg
    );

    // Persist pendingTransfer on backend
    try {
      await api.post(`/gear/${gearId}/transfer`, {
        notifId,
        to,
        from,
        requestedBy: currentUserId,
      });
    } catch (e) {
      console.warn('Failed to persist pending transfer', e);
    }

    // Create notifications on backend for the intended recipient
    try {
      await api.post('/notifications', {
        notifId,
        type: 'gear-transfer',
        userIds: [to.userId], // recipient userId
        payload: { gearId, from, to, requestedBy: currentUserId },
      });
    } catch (e) {
      console.warn('Failed to create notifications', e);
    }

    setShowTransferModal(false);
  }

  // Accept/Deny logic for pending transfer
  // (Removed legacy Firebase notification updater — handled by backend /notifications + /gear transfer routes)

// Accept/Deny handler for pending transfer (store + backend notifications)
const handlePendingAction = async (accept) => {
  if (!localGear || !localGear.pendingTransfer) return;
  setPendingAction(true);

  const pending = localGear.pendingTransfer;

  // Update global store first for instant UI
  setGear((prev) => {
    if (!Array.isArray(prev)) return [];
    return prev.map((row) => {
      if (row.id !== localGear.id) return row;

      const activity = Array.isArray(row.activity) ? row.activity : [];
      if (accept) {
        return {
          ...row,
          assignedTo: pending.to.userId,
          assignedProject: pending.to.projectId || "",
          pendingTransfer: null,
          activity: [
            ...activity,
            {
              type: "transfer-accepted",
              date: new Date().toISOString(),
              from: { assignedTo: pending.from.userId, assignedProject: pending.from.projectId },
              to:   { assignedTo: pending.to.userId,   assignedProject: pending.to.projectId },
              by: currentUserId,
            },
          ],
        };
      }
      return {
        ...row,
        pendingTransfer: null,
        activity: [
          ...activity,
          {
            type: "transfer-denied",
            date: new Date().toISOString(),
            from: { assignedTo: pending.from.userId, assignedProject: pending.from.projectId },
            to:   { assignedTo: pending.to.userId,   assignedProject: pending.to.projectId },
            by: currentUserId,
          },
        ],
      };
    });
  });

  // Update local detail view to reflect changes immediately
  setLocalGear((row) => {
    if (!row || row.id !== localGear.id) return row;
    const base = { ...row };
    const activity = Array.isArray(base.activity) ? base.activity : [];
    if (accept) {
      base.assignedTo = pending.to.userId;
      base.assignedProject = pending.to.projectId || "";
      base.pendingTransfer = null;
      base.activity = [
        ...activity,
        {
          type: "transfer-accepted",
          date: new Date().toISOString(),
          from: { assignedTo: pending.from.userId, assignedProject: pending.from.projectId },
          to:   { assignedTo: pending.to.userId,   assignedProject: pending.to.projectId },
          by: currentUserId,
        },
      ];
    } else {
      base.pendingTransfer = null;
      base.activity = [
        ...activity,
        {
          type: "transfer-denied",
          date: new Date().toISOString(),
          from: { assignedTo: pending.from.userId, assignedProject: pending.from.projectId },
          to:   { assignedTo: pending.to.userId,   assignedProject: pending.to.projectId },
          by: currentUserId,
        },
      ];
    }
    return base;
  });

  // Persist the gear decision to backend (moves assignment on accept, clears pending always)
  try {
    await api.post(`/gear/${localGear.id}/transfer/decision`, {
      decision: accept ? 'accepted' : 'denied',
      by: currentUserId,
    });
  } catch (e) {
    console.warn('Failed to persist transfer decision', e);
  }

  // Resolve notifications on backend for this notifId
  try {
    await api.post('/notifications/decision', {
      notifId: pending.notifId,
      decision: accept ? 'accepted' : 'denied',
    });
  } catch (e) {
    // Non-blocking: UI already updated
    console.warn('Failed to update notifications on backend', e);
  }

  setPendingHandled(true);
  setPendingAction(false);
};
  // Who can accept/deny?
  const me = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null') || null;
    } catch {
      return null;
    }
  })();
  const myRole = teamList.find((u) => u.id === me?.id)?.role || me?.role || "user";
  const canApprove =
    !!localGear.pendingTransfer &&
    (
      ["admin", "manager"].includes(myRole) ||
      localGear.pendingTransfer.to.userId === me?.id
    );

  // Admin check for delete activity
  const isAdmin = myRole === "admin";
  const handleDeleteActivity = async (idx) => {
    // compute the real index (list is reversed in UI)
    const activity = Array.isArray(localGear.activity) ? [...localGear.activity] : [];
    const trueIndex = activity.length - 1 - idx;
    if (trueIndex < 0 || trueIndex >= activity.length) return;

    // Update global store
    setGear((prev) => {
      if (!Array.isArray(prev)) return [];
      return prev.map((row) => {
        if (row.id !== localGear.id) return row;
        const nextActivity = [...activity];
        nextActivity.splice(trueIndex, 1);
        return { ...row, activity: nextActivity };
      });
    });

    // Update local detail view
    setLocalGear((row) => {
      if (!row || row.id !== localGear.id) return row;
      const nextActivity = [...activity];
      nextActivity.splice(trueIndex, 1);
      return { ...row, activity: nextActivity };
    });
  };

  if (!localGear) {
    return (
      <div className="flex flex-col items-center p-8 text-gray-500">
        <button
          className="mb-4 text-blue-600 hover:underline flex items-center"
          onClick={() => setCurrentPage("gear")}
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to Gear
        </button>
        Gear not found.
      </div>
    );
  }

  const assignedUser = teamList.find((u) => u.id === localGear.assignedTo);
  const assignedProject = projectsList.find((p) => p.id === localGear.assignedProject);

  return (
    <div className="w-full min-h-[90vh] flex flex-col items-center px-0 sm:px-4 py-3 sm:py-8 bg-[#f8fafd] pb-28 sm:pb-0">
      <div className="w-full max-w-5xl bg-white rounded-none sm:rounded-xl shadow-none sm:shadow-xl p-3 sm:p-8 md:px-10 relative">
        {/* Top: Back + Actions */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <div className="flex items-center justify-between w-full sm:w-auto gap-3">
            <button
              className="text-blue-600 flex items-center hover:underline w-fit"
              onClick={() => setCurrentPage("gear")}
              type="button"
            >
            <ArrowLeft size={20} className="mr-1" />
            Back
          </button>

            <button
              type="button"
              onClick={() => setShowEditModal(true)}
              disabled={!isAdminOrManager}
              title={isAdminOrManager ? "Edit gear" : "Only Admin/Manager can edit"}
              className={
                "ml-auto sm:ml-0 inline-flex items-center justify-center h-9 w-9 rounded-lg border " +
                (isAdminOrManager
                  ? "bg-white hover:bg-gray-50 text-gray-700"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed")
              }
            >
              <Settings size={18} />
            </button>
          </div>

          {/* Desktop/Tablet button */}
          <button
            className="hidden sm:flex bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2 font-semibold items-center justify-center w-full sm:w-auto"
            onClick={() => setShowTransferModal(true)}
          >
            <Repeat size={18} className="mr-2" />
            Transfer Equipment
          </button>
        </div>

        {/* Mobile sticky CTA */}
        <div className="sm:hidden fixed left-3 right-3 bottom-20 z-40">
          <button
            className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-4 py-3 font-semibold flex items-center justify-center w-full shadow-lg"
            onClick={() => setShowTransferModal(true)}
            type="button"
          >
            <Repeat size={18} className="mr-2" />
            Transfer Equipment
          </button>
        </div>

        {/* IMAGE + INFO ROW */}
        <div className="w-full flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-6">
          {/* Left: image + name + chips */}
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <img
              src={localGear.imageUrl || "/gear-placeholder.png"}
              alt={localGear.name}
              className="w-20 h-20 sm:w-32 sm:h-32 object-cover rounded-2xl bg-gray-100 border flex-shrink-0"
            />
            <div className="min-w-0">
              <div className="text-2xl md:text-3xl font-bold mb-1 text-left break-words">
                {localGear.name}
              </div>
              <div className="flex gap-2 flex-wrap mb-1">
                {localGear.category && (
                  <span className="bg-blue-100 text-blue-700 rounded-xl px-3 py-1 text-xs font-medium">
                    {localGear.category}
                  </span>
                )}
                {localGear.status && (
                  <span className="bg-gray-100 text-gray-700 rounded-xl px-3 py-1 text-xs font-medium">
                    {localGear.status}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right: assigned/project/serial */}
          <div className="w-full sm:w-auto sm:min-w-[260px] flex flex-col gap-2 text-sm md:text-base">
            <div className="bg-gray-50 rounded-xl px-3 py-2">
              <span className="font-semibold text-gray-700">Assigned To: </span>
              <span className="text-gray-800">
                {assignedUser
                  ? assignedUser.name || assignedUser.email
                  : <span className="text-gray-400">—</span>}
              </span>
            </div>

            <div className="bg-gray-50 rounded-xl px-3 py-2">
              <span className="font-semibold text-gray-700">Project: </span>
              <span className="text-gray-800">
                {assignedProject
                  ? assignedProject.name
                  : <span className="text-gray-400">—</span>}
              </span>
            </div>

            <div className="bg-gray-50 rounded-xl px-3 py-2">
              <span className="font-semibold text-gray-700">Serial #: </span>
              <span className="text-gray-800">
                {localGear.serialNumber || <span className="text-gray-400">—</span>}
              </span>
            </div>
          </div>
        </div>

        {/* --- PENDING TRANSFER NOTICE --- */}
        {localGear.pendingTransfer && !pendingHandled && (
          <div className="mb-4 p-4 rounded-xl bg-yellow-50 border border-yellow-300 flex flex-col md:flex-row items-center md:gap-3 gap-2">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-yellow-500" />
              <span className="font-bold text-yellow-800">
                Transfer Pending Acceptance
              </span>
            </div>
            <div className="flex-1">
              <div className="text-sm text-yellow-700">
                <div>
                  <b>To:</b>{" "}
                  <span className="font-semibold">
                    {displayUser(localGear.pendingTransfer.to.userId)}
                  </span>
                  {localGear.pendingTransfer.to.projectId && (
                    <>
                      {" "}
                      (<b>Project:</b>{" "}
                      <span className="font-semibold">
                        {displayProject(localGear.pendingTransfer.to.projectId)}
                      </span>
                      )
                    </>
                  )}
                </div>
                <div>
                  <b>From:</b>{" "}
                  <span className="font-semibold">
                    {displayUser(localGear.pendingTransfer.from.userId)}
                  </span>
                  {localGear.pendingTransfer.from.projectId && (
                    <>
                      {" "}
                      (<b>Project:</b>{" "}
                      <span className="font-semibold">
                        {displayProject(localGear.pendingTransfer.from.projectId)}
                      </span>
                      )
                    </>
                  )}
                </div>
                <div>
                  <span className="text-xs">
                    Requested:{" "}
                    {new Date(localGear.pendingTransfer.date).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            {canApprove && !pendingHandled && (
              <div className="flex gap-2 mt-2 md:mt-0">
                <button
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                  onClick={() => handlePendingAction(true)}
                  disabled={pendingAction}
                >
                  Accept
                </button>
                <button
                  className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                  onClick={() => handlePendingAction(false)}
                  disabled={pendingAction}
                >
                  Deny
                </button>
              </div>
            )}
          </div>
        )}

        {/* --- TABS (mobile only) --- */}
        <div className="md:hidden">
          <GearTabs tabs={TAB_LIST} activeTab={tab} onChange={setTab} />
        </div>

        {/* --- TAB CONTENT --- */}
        <div>
          {tab === "overview" && (
            <div className="px-2 py-2 text-gray-700">
              <div><span className="font-medium">Name:</span> {localGear.name}</div>
              <div><span className="font-medium">Serial #:</span> {localGear.serialNumber || <span className="text-gray-400">—</span>}</div>
              <div><span className="font-medium">Category:</span> {localGear.category}</div>
              <div><span className="font-medium">Status:</span> {localGear.status}</div>
              <div><span className="font-medium">Notes:</span> {localGear.notes || <span className="text-gray-400">—</span>}</div>
              <div><span className="font-medium">Date Added:</span> {localGear.dateAdded ? new Date(localGear.dateAdded).toLocaleDateString() : <span className="text-gray-400">—</span>}</div>
            </div>
          )}
          {tab === "activity" && (
            <div className="px-2 py-2 text-gray-700">
              {(!localGear.activity || localGear.activity.length === 0) ? (
                <div className="text-gray-400">No activity yet.</div>
              ) : (
                <ul className="space-y-3">
                  {localGear.activity.slice().reverse().map((act, i) => {
                    const showTrash = isAdmin;
                    if (["transfer-accepted", "transfer-denied"].includes(act.type)) {
                      return (
                        <li key={i} className="bg-blue-50 rounded px-4 py-2 text-sm">
                          <div className="flex items-center justify-between">
                            <div className="font-bold capitalize mb-1">
                              Transfer {act.type === "transfer-accepted" ? "Accepted" : "Denied"} — {act.date ? new Date(act.date).toLocaleString() : ""}
                            </div>
                            {showTrash && (
                              <button
                                className="text-red-500 hover:text-red-700 ml-3"
                                title="Delete Activity"
                                onClick={() => handleDeleteActivity(i)}
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                          <div>
                            <b>From:</b> {displayUser(act.from?.assignedTo)}
                            {act.from?.assignedProject && (
                              <> (<b>Project:</b> {displayProject(act.from.assignedProject)})</>
                            )}
                          </div>
                          <div>
                            <b>To:</b> {displayUser(act.to?.assignedTo)}
                            {act.to?.assignedProject && (
                              <> (<b>Project:</b> {displayProject(act.to.assignedProject)})</>
                            )}
                          </div>
                          <div>
                            <b>{act.type === "transfer-accepted" ? "Accepted" : "Denied"} by:</b> {displayUser(act.by)}
                          </div>
                        </li>
                      );
                    }
                    if (act.type === "archived" || act.type === "unarchived") {
                      return (
                        <li key={i} className="bg-gray-50 rounded px-4 py-2 text-sm">
                          <div className="flex items-center justify-between">
                            <div className="font-bold mb-1">
                              {act.type === "archived" ? "Archived" : "Restored"} —{" "}
                              {act.date ? new Date(act.date).toLocaleString() : ""}
                            </div>
                            {showTrash && (
                              <button
                                className="text-red-500 hover:text-red-700 ml-3"
                                title="Delete Activity"
                                onClick={() => handleDeleteActivity(i)}
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    }

                    if (act.type === "transfer-requested") {
                      return (
                        <li key={i} className="bg-yellow-50 rounded px-4 py-2 text-sm">
                          <div className="flex items-center justify-between">
                            <div className="font-bold mb-1">
                              Transfer Requested —{" "}
                              {act.date ? new Date(act.date).toLocaleString() : ""}
                            </div>
                            {showTrash && (
                              <button
                                className="text-red-500 hover:text-red-700 ml-3"
                                title="Delete Activity"
                                onClick={() => handleDeleteActivity(i)}
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                          <div>
                            <b>To:</b> {displayUser(act.to?.assignedTo || act.to?.userId)}
                            {act.to?.assignedProject || act.to?.projectId ? (
                              <> (<b>Project:</b> {displayProject(act.to?.assignedProject || act.to?.projectId)})</>
                            ) : null}
                          </div>
                        </li>
                      );
                    }

                    // Fallback for any future activity types so the list is never blank
                    return (
                      <li key={i} className="bg-gray-50 rounded px-4 py-2 text-sm">
                        <div className="flex items-center justify-between">
                          <div className="font-bold mb-1">
                            {act.action || act.type || "Update"} —{" "}
                            {act.date ? new Date(act.date).toLocaleString() : ""}
                          </div>
                          {showTrash && (
                            <button
                              className="text-red-500 hover:text-red-700 ml-3"
                              title="Delete Activity"
                              onClick={() => handleDeleteActivity(i)}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
          {tab === "documents" && (
            <div className="px-2 py-2 text-gray-500">
              <div className="text-sm">No documents uploaded.</div>
            </div>
          )}
          {tab === "images" && (
            <div className="px-2 py-2 flex flex-wrap gap-3">
              {localGear.imageUrl ? (
                <img
                  src={localGear.imageUrl}
                  alt={localGear.name}
                  className="w-28 h-28 object-cover rounded-xl border"
                />
              ) : (
                <div className="text-sm text-gray-400">No images uploaded.</div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Edit Gear Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center px-3">
          <div ref={editModalRef} className="relative z-50 w-full max-w-lg">
            <GearModal
              modalRef={editModalRef}
              gear={localGear}
              onClose={() => setShowEditModal(false)}
              onSave={handleEditSave}
              team={teamList}
              projects={projectsList}
              categories={Array.isArray(categories) ? categories : []}
              statuses={Array.isArray(statuses) ? statuses : []}
              fields={Array.isArray(fields) ? fields : []}
              isAdminOrManager={isAdminOrManager}
            />
          </div>
        </div>
      )}

      {/* Transfer Equipment Modal */}
      {showTransferModal && (
        <TransferEquipmentModal
          gearList={[localGear]}
          team={teamList}
          projects={projectsList}
          onClose={() => setShowTransferModal(false)}
          onTransfer={async (gearId, newAssignedTo, newProject) => {
            await handleGearTransfer(gearId, newAssignedTo, newProject);
            // localGear + global store are updated in handleGearTransfer
            setShowTransferModal(false);
          }}
        />
      )}
    </div>
  );
}
